import React, { useEffect, useState } from 'react';
import { listReports, getReportSettings, updateReportSettings, resetReportSettings } from '../../lib/reportStudioApi';
import { useClubIdentity } from '../../lib/useClubIdentity';
import SettingsFieldsForm from './SettingsFieldsForm';
import ReportPreviewMock from './ReportPreviewMock';
import { fmtDate, PrimaryBtn } from '../../pages/organization/finance/shared';

export default function CustomizeReportsSection({ canManage }) {
  const identity = useClubIdentity();
  const [reports, setReports] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState(null);
  const [useCustom, setUseCustom] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const notify = (m) => { setToast(m); setTimeout(() => setToast(null), 2500); };

  useEffect(() => { listReports().then(setReports); }, []);
  useEffect(() => {
    if (!selectedKey) return;
    getReportSettings(selectedKey).then((d) => { setDetail(d); setDraft(d.effective); setUseCustom(d.use_custom); });
  }, [selectedKey]);

  const sections = detail?.report?.linked ? detail.report.supports_sections : [];
  const contentOptions = detail?.report?.linked ? detail.report.supports_content : [];

  const save = async () => {
    setSaving(true);
    try {
      const d = await updateReportSettings(selectedKey, useCustom, useCustom ? draft : {});
      setDetail(d); setDraft(d.effective); notify('تم حفظ تخصيص التقرير');
      listReports().then(setReports);
    } catch { notify('فشل الحفظ'); }
    setSaving(false);
  };
  const resetToGeneral = async () => {
    const d = await resetReportSettings(selectedKey);
    setDetail(d); setDraft(d.effective); setUseCustom(false);
    notify('أُعيد التقرير إلى الإعدادات العامة'); listReports().then(setReports);
  };

  const grouped = reports.reduce((acc, r) => { (acc[r.section] = acc[r.section] || []).push(r); return acc; }, {});

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1.4fr_1fr]" data-testid="rs-customize-section">
      {toast && <div className="fixed top-16 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-[#16233b] px-4 py-2 text-sm text-white shadow-lg">{toast}</div>}
      <div className="rounded-xl border border-slate-200 bg-white p-2 max-h-[560px] overflow-y-auto" data-testid="rs-reports-list">
        {Object.entries(grouped).map(([section, items]) => (
          <div key={section} className="mb-2">
            <p className="px-2 py-1 text-[11px] font-bold text-slate-400">{section}</p>
            {items.map((r) => (
              <button key={r.key} onClick={() => setSelectedKey(r.key)} className={`flex w-full items-center justify-between rounded-lg px-2 py-2 text-right text-sm ${selectedKey === r.key ? 'bg-teal-50 text-teal-800' : 'hover:bg-slate-50 text-slate-700'}`} data-testid={`rs-report-item-${r.key}`}>
                <span>{r.label}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.linked ? (r.use_custom ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-500') : 'bg-amber-50 text-amber-700'}`}>
                  {r.linked ? (r.use_custom ? 'مخصص' : 'عام') : 'غير مربوط بعد'}
                </span>
              </button>
            ))}
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        {!selectedKey && <p className="text-sm text-slate-400">اختر تقريراً من القائمة لعرض تفاصيله وتخصيصه</p>}
        {selectedKey && !detail && <div className="h-40 animate-pulse rounded-lg bg-slate-100" />}
        {detail && (
          <div className="space-y-3">
            <div>
              <h3 className="text-base font-bold text-slate-900" data-testid="rs-report-title">{detail.report.label}</h3>
              <p className="text-xs text-slate-500">{detail.report.section} — {detail.report.linked ? 'مربوط بالاستوديو' : 'غير مربوط بعد'}</p>
              {reports.find((r) => r.key === selectedKey)?.last_exported_at && <p className="text-xs text-slate-400">آخر تصدير: {fmtDate(reports.find((r) => r.key === selectedKey).last_exported_at)}</p>}
            </div>
            {!detail.report.linked ? (
              <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="rs-report-not-linked">هذا التقرير غير مربوط بالاستوديو بعد — سيبقى يعمل بشكل طبيعي، وسيمكن تخصيصه لاحقاً.</div>
            ) : (
              <>
                <div className="flex gap-2">
                  <button onClick={() => setUseCustom(false)} className={`flex-1 rounded-lg border px-3 py-2 text-xs font-semibold ${!useCustom ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-slate-200 text-slate-500'}`} data-testid="rs-use-general-btn">استخدام الإعدادات العامة</button>
                  <button onClick={() => setUseCustom(true)} className={`flex-1 rounded-lg border px-3 py-2 text-xs font-semibold ${useCustom ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-slate-200 text-slate-500'}`} data-testid="rs-use-custom-btn">تخصيص مستقل</button>
                </div>
                {useCustom && <SettingsFieldsForm value={draft} onChange={setDraft} sections={sections} contentOptions={contentOptions} testPrefix="rs-custom" />}
                {canManage && (
                  <div className="flex flex-wrap justify-end gap-2 pt-2">
                    {detail.use_custom && <button onClick={resetToGeneral} className="rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-600" data-testid="rs-reset-btn">إعادة إلى الإعدادات العامة</button>}
                    <PrimaryBtn onClick={save} disabled={saving} data-testid="rs-customize-save-btn">{saving ? 'جارٍ الحفظ...' : 'حفظ'}</PrimaryBtn>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div>
        <p className="mb-2 text-xs font-bold text-slate-500">معاينة مباشرة</p>
        {draft ? <ReportPreviewMock settings={draft} identity={identity} reportTitle={detail?.report?.label} /> : <div className="h-40 animate-pulse rounded-xl bg-slate-100" />}
      </div>
    </div>
  );
}
